/**
 * ReportsPage — Rapports comptables (Phase 8, module 3).
 * Sélecteur mois ou plage custom, preview totaux, export CSV / PDF.
 */
import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, FileSpreadsheet, FileText } from "lucide-react";
import { formatCurrencyBE } from "@/lib/currency";

const MONTHS = [
  "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
  "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre",
];

function useYearsList() {
  const now = new Date();
  const arr = [];
  for (let y = now.getFullYear(); y >= now.getFullYear() - 3; y--) arr.push(y);
  return arr;
}

export default function ReportsPage() {
  const years = useYearsList();
  const now = new Date();
  const [mode, setMode] = useState("month");           // "month" | "custom"
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(null);

  const params = useMemo(() => {
    if (mode === "month") return { year, month };
    if (fromDate && toDate) return { from: fromDate, to: toDate };
    return null;
  }, [mode, year, month, fromDate, toDate]);

  useEffect(() => {
    if (!params) { setPreview(null); return; }
    setLoading(true);
    const search = new URLSearchParams(params).toString();
    api.get(`/reports/preview?${search}`)
      .then((r) => setPreview(r.data))
      .catch((e) => { toast.error(formatApiError(e)); setPreview(null); })
      .finally(() => setLoading(false));
  }, [params]);

  const download = async (fmt) => {
    if (!params) return;
    setDownloading(fmt);
    try {
      const endpoint = mode === "month" ? "/reports/monthly" : "/reports/custom";
      const search = new URLSearchParams({ ...params, format: fmt }).toString();
      const res = await api.get(`${endpoint}?${search}`, { responseType: "blob" });
      const mime = fmt === "csv" ? "text/csv;charset=utf-8" : "application/pdf";
      const blob = new Blob([res.data], { type: mime });
      const url = URL.createObjectURL(blob);
      const label = mode === "month"
        ? `${year}-${String(month).padStart(2, "0")}`
        : `${fromDate}_${toDate}`;
      const a = document.createElement("a");
      a.href = url;
      a.download = `rapport-comptable-${label}.${fmt}`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      toast.success(`Rapport ${fmt.toUpperCase()} téléchargé`);
    } catch (e) {
      // responseType: "blob" → le body erreur reste un blob. On tente de le lire en JSON.
      let msg = formatApiError(e);
      const blob = e?.response?.data;
      if (blob instanceof Blob) {
        try {
          const text = await blob.text();
          const parsed = JSON.parse(text);
          if (parsed?.detail) msg = parsed.detail;
        } catch (parseErr) {
          console.warn("[Reports] Blob error body not JSON — fallback to formatApiError:", parseErr?.message);
        }
      }
      toast.error(msg);
    } finally {
      setDownloading(null);
    }
  };

  const totals = preview?.totals || {};

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div>
        <h1 className="text-2xl font-bold">Rapports comptables</h1>
        <p className="text-sm text-slate-400">
          Export mensuel ou sur période libre : factures + notes de crédit + solde net (CSV Excel-compatible BE et PDF synthèse).
        </p>
      </div>

      <Card className="border-slate-800 bg-slate-900/40">
        <CardContent className="space-y-4 p-6">
          <div className="flex gap-2 border-b border-slate-800 pb-3">
            <button
              type="button"
              onClick={() => setMode("month")}
              className={`rounded px-3 py-1 text-sm ${mode === "month"
                ? "bg-[#C4553B] text-white"
                : "border border-slate-700 bg-slate-800/60 text-slate-300"}`}
              data-testid="reports-mode-month"
            >
              Par mois
            </button>
            <button
              type="button"
              onClick={() => setMode("custom")}
              className={`rounded px-3 py-1 text-sm ${mode === "custom"
                ? "bg-[#C4553B] text-white"
                : "border border-slate-700 bg-slate-800/60 text-slate-300"}`}
              data-testid="reports-mode-custom"
            >
              Plage libre
            </button>
          </div>

          {mode === "month" ? (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-slate-300">Mois</Label>
                <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
                  <SelectTrigger className="border-slate-700 bg-slate-950/40 text-slate-100"
                                 data-testid="reports-month-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="border-slate-700 bg-slate-900">
                    {MONTHS.map((m, i) => (
                      <SelectItem key={i + 1} value={String(i + 1)}>{m}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-slate-300">Année</Label>
                <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
                  <SelectTrigger className="border-slate-700 bg-slate-950/40 text-slate-100"
                                 data-testid="reports-year-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="border-slate-700 bg-slate-900">
                    {years.map((y) => (
                      <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-slate-300">Du</Label>
                <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)}
                       className="border-slate-700 bg-slate-950/40 text-slate-100"
                       data-testid="reports-from-input" />
              </div>
              <div>
                <Label className="text-slate-300">Au</Label>
                <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)}
                       className="border-slate-700 bg-slate-950/40 text-slate-100"
                       data-testid="reports-to-input" />
              </div>
            </div>
          )}

          {loading && (
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <Loader2 className="h-3 w-3 animate-spin" /> Calcul en cours…
            </div>
          )}

          {preview && !loading && (
            <div className="rounded border border-slate-800 bg-slate-950/30 p-4 text-sm"
                 data-testid="reports-preview">
              <div className="mb-2 text-xs uppercase tracking-wide text-slate-500">Aperçu</div>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <div>
                  <div className="text-[11px] text-slate-500">Lignes</div>
                  <div className="text-lg font-semibold text-slate-100 tabular-nums"
                       data-testid="reports-preview-count">{preview.row_count}</div>
                </div>
                <div>
                  <div className="text-[11px] text-slate-500">Total facturé TTC</div>
                  <div className="text-lg font-semibold text-slate-100 tabular-nums"
                       data-testid="reports-preview-facture-ttc">
                    {formatCurrencyBE(totals.total_facture_ttc || 0)}
                  </div>
                </div>
                <div>
                  <div className="text-[11px] text-slate-500">Total crédité TTC</div>
                  <div className="text-lg font-semibold text-[#E9A98F] tabular-nums">
                    {formatCurrencyBE(totals.total_credite_ttc || 0)}
                  </div>
                </div>
                <div>
                  <div className="text-[11px] text-slate-500">Solde net TTC</div>
                  <div className="text-lg font-semibold text-[#C4553B] tabular-nums"
                       data-testid="reports-preview-solde-ttc">
                    {formatCurrencyBE(totals.solde_net_ttc || 0)}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Phase Rentab-3 (13/09/2026) — Bandeau info colonnes Rentab. */}
          {preview?.rentab && !loading && (
            <div
              className="flex items-start gap-2 rounded border border-amber-500/30 bg-amber-500/5 p-3 text-xs text-amber-200"
              data-testid="reports-rentab-banner"
            >
              <span aria-hidden="true">ℹ️</span>
              <div>
                <div className="font-medium text-amber-100">
                  Nouvelles colonnes Rentab dans le CSV
                </div>
                <p className="mt-0.5 text-amber-200/90">
                  Le fichier CSV inclut désormais 3 colonnes :{" "}
                  <span className="font-mono">cout_mo_estime_htva</span>,{" "}
                  <span className="font-mono">cout_mo_realise_htva</span>,{" "}
                  <span className="font-mono">sous_traitance_deduite_htva</span>. Ces
                  colonnes sont renseignées <b>uniquement sur la 1ère facture
                  de chaque chantier dans la période</b>{" "}
                  (cellules vides sur les factures suivantes du même chantier) pour
                  garantir une somme comptable correcte. Données disponibles à
                  partir du{" "}
                  <b>
                    {(() => {
                      const [y, m, d] = (preview.rentab.columns_available_from || "").split("-");
                      return d && m && y ? `${d}/${m}/${y}` : preview.rentab.columns_available_from;
                    })()}
                  </b>
                  .
                </p>
                {preview.rentab.rows_with_rentab_data > 0 && (
                  <p
                    className="mt-1 text-amber-200/80"
                    data-testid="reports-rentab-banner-count"
                  >
                    {preview.rentab.rows_with_rentab_data} chantier
                    {preview.rentab.rows_with_rentab_data > 1 ? "s" : ""} concerné
                    {preview.rentab.rows_with_rentab_data > 1 ? "s" : ""} dans la période.
                  </p>
                )}
              </div>
            </div>
          )}

          <div className="flex gap-2">
            <Button
              onClick={() => download("csv")}
              disabled={!preview || downloading !== null}
              className="bg-[#C4553B] text-white hover:bg-[#A8402C]"
              data-testid="reports-download-csv"
            >
              {downloading === "csv" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                : <FileSpreadsheet className="mr-2 h-4 w-4" />}
              Exporter CSV
            </Button>
            <Button
              variant="outline"
              onClick={() => download("pdf")}
              disabled={!preview || downloading !== null}
              className="border-slate-700 bg-slate-800/60 text-slate-200 hover:bg-slate-800"
              data-testid="reports-download-pdf"
            >
              {downloading === "pdf" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                : <FileText className="mr-2 h-4 w-4" />}
              Exporter PDF
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
